import { useState, useEffect, useCallback } from 'react';
import { toNumeric } from '../utils/transponder';
import { authFetch, postJson, AUTH_TOKEN_KEY } from '../hooks/useApi';
import AuthModal from '../components/AuthModal';
import { AuthContext } from './auth-context';
import { useQueryClient } from '@tanstack/react-query';

function readToken() { return localStorage.getItem(AUTH_TOKEN_KEY); }
function writeToken(t) {
  if (t) localStorage.setItem(AUTH_TOKEN_KEY, t);
  else localStorage.removeItem(AUTH_TOKEN_KEY);
}

// Markeert dat de huidige sessie door een admin is gestart via "Inloggen als"
// (mgmt-impersonate), zodat de UI dit kan tonen en makkelijk kan beëindigen.
// sessionStorage (niet localStorage) zodat het niet overleeft naar een nieuw
// tabblad of na een echte login/registratie.
const IMPERSONATE_KEY = 'impersonating';

export function AuthProvider({ children, onAuthenticated, onImpersonationEnd }) {
  const queryClient = useQueryClient();
  const [user, setUser]                 = useState(null);
  const [preferences, setPreferences]   = useState(null);
  const [transponders, setTransponders] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [modalOpen, setModalOpen]       = useState(false);
  const [impersonating, setImpersonating] = useState(() => sessionStorage.getItem(IMPERSONATE_KEY) === '1');

  const refresh = useCallback(() => {
    if (!readToken()) { setUser(null); setPreferences(null); setTransponders([]); setLoading(false); return; }
    authFetch('/api/me')
      .then(r => r.json())
      .then(d => {
        if (d.loggedIn) {
          setUser(d.user ?? null);
          setPreferences(d.preferences ?? { pauseThresholdSeconds: 70, defaultLane: 'binnenbaan' });
          setTransponders(d.transponders ?? []);
        } else { writeToken(null); setUser(null); setPreferences(null); setTransponders([]); } // token verlopen/ongeldig
        setLoading(false);
      })
      .catch(() => { setUser(null); setPreferences(null); setTransponders([]); setLoading(false); });
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const owns = useCallback((transponder) => {
    if (!transponder) return false;
    const n = toNumeric(transponder);
    if (n == null) return false;
    return transponders.some(t => String(t.numeric) === String(n));
  }, [transponders]);

  const savePreferences = useCallback(async nextPreferences => {
    const { ok, data } = await postJson('/api/user-preferences', nextPreferences);
    if (ok) setPreferences(data.preferences);
    return { ok, error: ok ? null : (data.error || 'Voorkeuren opslaan mislukt') };
  }, []);

  // Verwerk een geslaagd auth-antwoord ({ token, user }). Na een expliciete
  // login/registratie/reset navigeren we naar het profiel (onAuthenticated).
  const applyAuth = useCallback((data, { impersonated = false } = {}) => {
    queryClient.clear();
    writeToken(data.token);
    setUser(data.user ?? null);
    setModalOpen(false);
    if (impersonated) { sessionStorage.setItem(IMPERSONATE_KEY, '1'); setImpersonating(true); }
    refresh();
    onAuthenticated?.();
  }, [refresh, onAuthenticated, queryClient]);

  const signIn = useCallback(async (email, password) => {
    const { ok, data } = await postJson('/api/auth-login', { email, password });
    if (ok) applyAuth(data);
    return { ok, error: ok ? null : (data.error || 'Inloggen mislukt') };
  }, [applyAuth]);

  const signUp = useCallback(async (email, password, name) => {
    const { ok, data } = await postJson('/api/auth-register', { email, password, name });
    if (ok) applyAuth(data);
    return { ok, error: ok ? null : (data.error || 'Registreren mislukt') };
  }, [applyAuth]);

  const sendReset = useCallback(async (email) => {
    const { ok, data } = await postJson('/api/auth-forgot', { email });
    return { ok, error: ok ? null : (data.error || 'Er ging iets mis') };
  }, []);

  const resetPassword = useCallback(async (token, password) => {
    const { ok, data } = await postJson('/api/auth-reset', { token, password });
    if (ok) applyAuth(data);
    return { ok, error: ok ? null : (data.error || 'Wachtwoord opnieuw instellen mislukt') };
  }, [applyAuth]);

  // Admin-only: log in als een ander account (zie /admin → Gebruikers). Zelfde
  // respons-vorm als signIn/signUp, dus applyAuth kan hergebruikt worden.
  const impersonate = useCallback(async (userId) => {
    const { ok, data } = await postJson('/api/mgmt-impersonate', { userId });
    if (ok) applyAuth(data, { impersonated: true });
    return { ok, error: ok ? null : (data.error || 'Inloggen als gebruiker mislukt') };
  }, [applyAuth]);

  const login  = useCallback(() => setModalOpen(true), []);
  const logout = useCallback(() => {
    queryClient.clear();
    writeToken(null);
    sessionStorage.removeItem(IMPERSONATE_KEY);
    setImpersonating(false);
    setUser(null);
    setPreferences(null);
    setTransponders([]);
  }, [queryClient]);

  // Beëindig een impersonatie-sessie en breng de admin terug naar /admin.
  const stopImpersonating = useCallback(() => {
    logout();
    onImpersonationEnd?.();
  }, [logout, onImpersonationEnd]);

  const value = {
    loading,
    loggedIn: !!user,
    user,
    preferences,
    transponders,
    owns,
    refresh,
    savePreferences,
    login,
    logout,
    signIn,
    signUp,
    sendReset,
    resetPassword,
    impersonating,
    impersonate,
    stopImpersonating,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {modalOpen && (
        <AuthModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSignIn={signIn}
          onSignUp={signUp}
          onForgot={sendReset}
        />
      )}
    </AuthContext.Provider>
  );
}
